import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

import CommonLayout from "../../../Common/Layout";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import AddIcon from "@mui/icons-material/Add";

import ComponentSuspense from "../../../Common/ComponentSuspense";
import MultipleChipInput from "../../../Common/MultipleChipInput";
import { ButtonUI, ConfirmPopup } from "../../../Common";
import { FormTile } from "../../../Common/Tile";

import WorkspaceService from "../../../../service/workspace";
import DataPipelineService from "../../../../service/dataPipelineService";

import { useCronReducer } from "../../../../util/cron";
import { regexBasicEmail } from "../../../../util/regularExpress";
import { DETAUL_CRON_VALUE, JOB_BY_TYPE } from "../utils/JobData";
import NextJobForm from "../utils/NextJobForm";
import Scheduler from "../utils/Scheduler";
import {
  arrangeSchema,
  base64Decode,
  base64Encode,
  checkIsClusterExist,
  checkIsSchemaExist,
  getUUID,
  setPrevState,
} from "../../../../util/common";

import { showToast } from "../../../../store/actions";
import { navScheduledJobs } from "../../../kmk/AppData";

import AddJobForm from "./AddJobForm";
import AdditionalData from "./AdditionalData";
import CloneJob from "../CloneJob";

const KMKTextEditor = React.lazy(() => import("../../../Common/KMKTextEditor"));

const useStyles = makeStyles()((theme) => ({
  PanelTitle: {
    color: "#ed6c02",
    padding: "24px 24px 12px 0",
    minHeight: 40,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
    "& .MuiPaper-root": {
      height: "auto",
    },
  },
}));

const FIRST_KEY = getUUID();

export default function Notofication() {
  const { Workspace_id, Job_id } = useParams();
  const user = useSelector((state) => state.user.currentUser);
  const workspace = useSelector((state) => state.workspace);
  const config = useSelector((state) => state.config);
  const clusterList = useSelector((state) => state.clusterList);
  const { Name: workspaceName, accessRole, DBName } = workspace.workspace;
  const { defaultSchema, defaultCluster } = config;
  const currentUserFullName = useMemo(() => `${user.FirstName} ${user.LastName}`, [user.FirstName, user.LastName]);

  const [openClone, setOpenClone] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [locations, setLocations] = useState([]);

  const [jobState, setJobState] = useState({
    name: "",
    jobType: "",
    Type: "email",
    timeZone: "",
    nextRunTime: "",
    loading: false,
    scheduleEnabled: false,
    closeSelect: false,
    DataPipeline_id: "",
    isEntryJob: false,
    subject: "",
    body: "",
  });

  const addNewSection = useMemo(
    () => ({
      runJobAsUserId: user.User_id,
      runJobAsUserFullName: currentUserFullName,
      database: DBName || "",
      schema: defaultSchema || "",
      Cluster: defaultCluster || "",
      includeResult: false,
      queryResultAs: 1,
      queryLastUpdatedByName: "",
      query: "",
      queryError: "",
      location: "SNOWFLAKE",
    }),
    [DBName, currentUserFullName, defaultCluster, defaultSchema, user.User_id]
  );

  const [additionalDataSections, setAdditionalDataKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(FIRST_KEY);

  const [variableError, setVariableError] = useState(() => ({
    [FIRST_KEY]: { message: "", schemaError: "" },
  }));

  const { variableNames, fileNames } = useMemo(
    () =>
      additionalDataSections.reduce(
        (acc, item) => {
          if (item?.variable?.trim()) acc.variableNames.push(item.variable.trim().toLowerCase());
          if (item?.fileName?.trim()) acc.fileNames.push(item.fileName.trim().toLowerCase());
          return acc;
        },
        { variableNames: [], fileNames: [] }
      ),
    [additionalDataSections]
  );

  const selectedSectionIndex = useMemo(
    () => additionalDataSections.findIndex((item) => item?.key === selectedKey),
    [additionalDataSections, selectedKey]
  );

  const [nextJobState, setNextJobState] = useState({
    nextJobToggle: false,
    nextDataPipelineId: "",
    nextJobName: "",
    nextJobId: "",
    activeSchedule: "",
    errorDataPipelineId: "",
    errorJobId: "",
    errorSchedule: "",
  });

  const [deleteJobState, setDeleteJobState] = useState({
    openConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [emails, setEmails] = useState([]);
  const [inputValues, setInputValues] = useState("");
  const [participantsExcludeVisitor, setParticipantsExcludeVisitor] = useState([]);

  const [dataPipelineList, setDataPipelineList] = useState([]);
  // Adding only query for now as it is the only property of which we are require previous state
  const [prevJobState, setPrevJobState] = useState(() => ({ [FIRST_KEY]: { runJobAsUserId: "", query: "" } }));

  const nextJobIdRef = useRef(nextJobState.nextJobId);
  const errorJobIdRef = useRef(nextJobState.errorJobId);
  const emailBodyref = useRef(null);
  const locationSchemasRef = useRef({});
  const locationClustersRef = useRef({ SNOWFLAKE: clusterList });

  // For Schedule Panel
  const [values, dispatchValues] = useCronReducer(DETAUL_CRON_VALUE);

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClear = useCallback(() => {
    setEmails([]);
    setInputValues("");
  }, []);

  const handleEmailChange = useCallback((newValue) => {
    const lastEmail = newValue[newValue.length - 1];
    if (regexBasicEmail(lastEmail)) setEmails(newValue);
    else setInputValues(lastEmail || "");
  }, []);

  const onDelete = useCallback((option) => setEmails((prev) => prev.filter((email) => email !== option)), []);

  const processEmails = useCallback(
    (emailString) => {
      const emailArray = emailString.split(",").map((email) => email.trim());

      let newEmails = [...emails];
      emailArray.forEach((email) => {
        if (regexBasicEmail(email) && !newEmails.includes(email)) newEmails.push(email.toLowerCase());
      });

      setEmails(newEmails);
      setInputValues("");
    },
    [emails]
  );

  const handleKeyPress = useCallback(
    (event) => {
      const trimmedValue = inputValues?.trim();
      if (event.key === "Enter" && emails?.some((email) => email?.toLowerCase() === trimmedValue?.toLowerCase())) {
        event.preventDefault();
        setInputValues("");
      }
      if (event.key === ",") {
        event.preventDefault();
        if (trimmedValue) processEmails(trimmedValue);
      }
    },
    [emails, inputValues, processEmails]
  );

  const handlePaste = useCallback(
    (event) => {
      const pastedData = event.clipboardData.getData("text");
      event.preventDefault();
      if (pastedData) processEmails(pastedData);
    },
    [processEmails]
  );

  const handleCancel = () => {
    setPrevState(setDeleteJobState, { openConfirm: false });
  };

  const deleteJob = async () => {
    setPrevState(setDeleteJobState, { loadingForConfirm: true });

    const wsService = new WorkspaceService();
    const result = await wsService.deleteNotificationJob(Workspace_id, Job_id);

    if (result.success) {
      dispatch(showToast({ message: result.message, type: "success" }));
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setDeleteJobState, { openConfirm: false, loadingForConfirm: false });
  };

  const compareQuery = useCallback((oldQuery, newQuery) => {
    oldQuery = oldQuery.trim().toLowerCase();
    newQuery = newQuery.trim().toLowerCase();
    return oldQuery !== newQuery;
  }, []);

  const handleSave = async (e, clone = false) => {
    setPrevState(setJobState, { loading: true });

    const sections = additionalDataSections.map(
      ({ key, query, runJobAsUserId, runJobAsUserFullName, ...other }, index) => {
        const queryComparision = compareQuery(prevJobState[key]?.query ?? "", query);
        const shouldUseCurrentUser = accessRole !== 1 && queryComparision;
        const userConfig = {
          runJobAsUserId: shouldUseCurrentUser ? user.User_id : runJobAsUserId,
          runJobAsUserFullName: shouldUseCurrentUser ? currentUserFullName : runJobAsUserFullName,
        };

        return {
          ...other,
          key,
          query,
          runJobAsUserId,
          runJobAsUserFullName,
          queryResultAs: other.queryResultAs ?? 1,
          Cluster: other.Cluster ?? defaultCluster,
          ...(other.location === "SNOWFLAKE" ? userConfig : {}),
          queryLastUpdatedByName: !Job_id || queryComparision ? currentUserFullName : other.queryLastUpdatedByName,
          fileName: other.fileName || `EXPORTED_DATA_${index + 1}`,
        };
      }
    );

    const submitData = {
      name: jobState.name,
      Type: jobState.Type,
      location: jobState.location,
      DataPipeline_id: jobState.DataPipeline_id,
      schedule: values.cronValue || DETAUL_CRON_VALUE,
      scheduleEnabled: jobState.scheduleEnabled || false,
      enableNextJob: nextJobState.nextJobToggle,
      ...(nextJobState.nextJobToggle
        ? {
            nextPipeline: nextJobState.nextDataPipelineId,
            nextJob: nextJobState.nextJobId,
            errorPipeline: nextJobState.errorDataPipelineId,
            errorJob: nextJobState.errorJobId,
            disableNextJob: nextJobState.activeSchedule,
            disableErrorJob: nextJobState.errorSchedule,
          }
        : {
            nextPipeline: null,
            nextJob: null,
            errorPipeline: null,
            errorJob: null,
            disableNextJob: null,
            disableErrorJob: null,
          }),
      isEntryJob: jobState.isEntryJob,
      subject: jobState.subject,
      body: await base64Encode(jobState.body ?? ""),
      emails,
      additionalDataConfig: sections,
    };

    let response = null;
    const wsService = new WorkspaceService();
    if (!Job_id) response = await wsService.createNotificationJob(submitData, Workspace_id);
    else response = await wsService.updateNotificationJob(submitData, Workspace_id, Job_id);

    if (response.success) {
      if (clone) {
        setPrevState(setJobState, { loading: false });
        return true;
      }

      dispatch(showToast({ message: response.message, type: "success" }));
      navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setJobState, { loading: false });
  };

  const handleCancelClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
  }, [Workspace_id, navigate]);

  const resetNextJob = () =>
    setPrevState(setNextJobState, {
      activeSchedule: "",
      nextJobToggle: false,
      nextJobName: "",
      nextJobId: "",
      errorJobId: "",
      errorSchedule: "",
    });

  const confirmPipelineChange = useCallback((e) => {
    setJobState((prev) => ({
      ...prev,
      DataPipeline_id: e.target.value,
      ...(prev.DataPipeline_id ? { isEntryJob: false } : {}),
    }));
    resetNextJob();
  }, []);

  const getSchemaList = useCallback(
    async (location = "SNOWFLAKE") => {
      const availableSchemas = locationSchemasRef.current[location]?.rawSchemas;
      if (!!availableSchemas) return availableSchemas;

      const wsService = new WorkspaceService();
      let schemas = [];

      if (location === "SNOWFLAKE") {
        const { success, data } = await wsService.getSchemaListByUser();
        if (!success) return {};
        schemas = arrangeSchema(
          data,
          (schemasByDB) => (locationSchemasRef.current[location] = { schemas: schemasByDB, rawSchemas: data })
        );
      } else {
        const { success, data } = await wsService.getExternalSnowflakeSchemas(Workspace_id, location, {
          schemasOnly: true,
        });
        if (!success) return {};
        schemas = arrangeSchema(
          data,
          (schemasByDB) => (locationSchemasRef.current[location] = { schemas: schemasByDB, rawSchemas: data })
        );
      }
      return schemas;
    },
    [Workspace_id]
  );

  const getExternalSnowflakeCluster = useCallback(
    async (location) => {
      const avaialbleClusters = locationClustersRef.current[location];
      if (!!avaialbleClusters) return avaialbleClusters;

      if (!location || location === "SNOWFLAKE") return [];
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getExternalSnowflakeCluster(Workspace_id, location);
      if (success && data?.length) {
        const clusters = data?.map((Cluster) => ({ value: Cluster?.name || "" }));
        locationClustersRef.current[location] = clusters;
        return clusters;
      }
      locationClustersRef.current[location] = [];
      return [];
    },
    [Workspace_id]
  );

  const generateName = useCallback(() => {
    const makeVar = (count) => `$ADDITIONAL_DATA_${count}$`;
    const makeFile = (count) => `Exported_Data_${count}`;

    let varIndex = variableNames.length + 1;
    let newVariableName = makeVar(varIndex);
    while (variableNames.includes(newVariableName.toLowerCase())) {
      varIndex += 1;
      newVariableName = makeVar(varIndex);
    }

    let fileIndex = fileNames.length + 1;
    let newFileName = makeFile(fileIndex);
    while (fileNames.includes(newFileName.toLowerCase())) {
      fileIndex += 1;
      newFileName = makeFile(fileIndex);
    }

    const key = getUUID();
    setAdditionalDataKeys((prev) => [
      ...prev,
      {
        key,
        ...addNewSection,
        variable: newVariableName,
        fileName: newFileName,
      },
    ]);
  }, [addNewSection, fileNames, variableNames]);

  useEffect(() => {
    //Get Location List for "Connection" Dropdown
    const getLocationList = async () => {
      const wsService = new WorkspaceService();
      const locations = await wsService.getLocationList(Workspace_id);

      return locations
        .filter((item) => ["snowflake"].includes(item.Detail.type.toLowerCase()))
        .map(({ Stage_id, Name, Detail }) => ({ value: Stage_id, text: Name, type: Detail?.type || "" }));
    };

    const getJobSchedule = async (locations) => {
      const wsService = new WorkspaceService();
      const response = await wsService.getNotificationSchedule(Workspace_id, Job_id);
      if (response === "Unauthorized" || !response.success)
        return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

      if (response.success) {
        dispatchValues({ type: "set_values", value: response.data.Schedule });

        setPrevState(setNextJobState, {
          nextDataPipelineId: response.data.NextPipeline_id || "",
          nextJobId: response.data.NextJob_id || nextJobIdRef.current,
          errorDataPipelineId: response.data.ErrorPipeline_id || "",
          errorJobId: response.data.ErrorJob_id || errorJobIdRef.current,
          nextJobToggle: response.data.enableNextJob || false,
        });

        setEmails(response.data.Detail?.emails ?? []);

        const prevState = {};
        const additionalDataSections = (
          response.data.Detail?.additionalDataConfig ?? [
            { key: FIRST_KEY, variable: "$ADDITIONAL_DATA$", ...addNewSection },
          ]
        ).map((item) => {
          const query = decodeURIComponent(item.query || "");

          prevState[item.key] = {};
          prevState[item.key]["runJobAsUserId"] = item.runJobAsUserId || "";
          prevState[item.key]["query"] = query;

          return { ...item, query };
        });

        setPrevState(setPrevJobState, { ...prevState });

        return {
          name: response.data.Name,
          DataPipeline_id: response.data.DataPipeline_id,

          timeZone: response.data.ServerTimeZone,
          scheduleEnabled: response.data.Enabled,
          nextRunTime: response.data.nextRun,

          subject: response.data.Detail?.subject || "",
          body: await base64Decode(response.data.Detail?.body ?? ""),

          additionalDataSections,
        };
      } else return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    };

    const getWorkspaceParticipants = async () => {
      const wsService = new WorkspaceService();
      const { data, success } = await wsService.getWorkspaceParticipants(Workspace_id, [1, 2]);
      return success ? data : [];
    };

    const getTimezone = async () => {
      const wsService = new WorkspaceService();
      const result = await wsService.getTimezone(Workspace_id);
      return result.data;
    };

    const getDataPipelineList = async () => {
      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipelineList(Workspace_id);
      if (response.success && response.data?.dataPipelineList?.length) return response.data.dataPipelineList;
      return [];
    };

    async function fetchData() {
      if (clusterList === false) return;
      let initialState = {};

      locationClustersRef.current["SNOWFLAKE"] = clusterList;

      const [locations, dataPipelines, workspaceParticipatns] = await Promise.all([
        getLocationList(),
        getDataPipelineList(),
        getWorkspaceParticipants(),
        getSchemaList("SNOWFLAKE"), // No need for variable
      ]);

      // no need to await this fetched schema list is for create job and will not be of use in this fun
      if (!Job_id) getSchemaList();

      setParticipantsExcludeVisitor(workspaceParticipatns);

      // Adding entry for current workspace option
      const snowflakeLocation = {
        value: "SNOWFLAKE",
        text: workspaceName + " (Current Workspace DB)",
        type: "snowflake",
      };

      setLocations([snowflakeLocation, ...locations]);

      if (Job_id) {
        initialState = await getJobSchedule();
        for (const section of initialState.additionalDataSections) {
          const selectedLocationType = locations.find(({ value }) => value === section?.location)?.type || "snowflake";

          if (selectedLocationType === "snowflake") {
            const schemasByDB = await getSchemaList(section.location);
            const isSchemaExist = checkIsSchemaExist(schemasByDB, section.database, section.schema);

            if (!isSchemaExist) {
              section.database = "";
              section.schema = "";
            }

            let clusters = [];
            if (section.location === "SNOWFLAKE") clusters = clusterList || [];
            else if (selectedLocationType === "snowflake") {
              clusters = await getExternalSnowflakeCluster(section.location);
            }

            const selectedClusterName = section.Cluster || (selectedLocationType === "snowflake" ? defaultCluster : "");

            // Check existence
            const isClusterExist = checkIsClusterExist(clusters, selectedClusterName);
            section.Cluster = (isClusterExist ? section.Cluster : defaultCluster) ?? "";

            setPrevState(setVariableError, {
              [section.key]: {
                message: "",
                schemaError: !isSchemaExist,
                userNotExist: !workspaceParticipatns.some((_user) => {
                  return _user.User_id === section.runJobAsUserId;
                }),
                clusterError: !isClusterExist,
              },
            });
          }

          if (!section.runJobAsUserId) {
            section.runJobAsUserId = user.User_id;
            section.runJobAsUserFullName = currentUserFullName;
          }
        }
      } else {
        initialState.timeZone = await getTimezone();
        initialState.additionalDataSections = [
          { ...addNewSection, key: FIRST_KEY, variable: "$ADDITIONAL_DATA$", fileName: "Exported_Data" },
        ];
        setPrevJobState({ [FIRST_KEY]: { runJobAsUserId: "", query: "" } });

        setPrevState(setVariableError, {
          [FIRST_KEY]: { message: "", schemaError: false, userNotExist: false },
        });
      }

      if (!initialState.DataPipeline_id) {
        initialState.DataPipeline_id = dataPipelines?.find((pipeline) => pipeline.IsDefault === 1)?.DataPipeline_id;
      }

      setDataPipelineList(dataPipelines);

      let isEntryJob = false;
      if (Job_id) {
        const currentPipeline = dataPipelines.find(
          ({ DataPipeline_id }) => parseInt(DataPipeline_id) === parseInt(initialState.DataPipeline_id)
        );
        isEntryJob = currentPipeline.EntryJob_id === parseInt(Job_id);
      }

      setSelectedKey(initialState.additionalDataSections[0].key);
      setAdditionalDataKeys(initialState.additionalDataSections);

      setPrevState(setJobState, { ...initialState, loading: false, isEntryJob });
      setInitialLoading(false);
    }

    fetchData();
  }, [
    DBName,
    Job_id,
    Workspace_id,
    addNewSection,
    clusterList,
    currentUserFullName,
    defaultCluster,
    defaultSchema,
    dispatchValues,
    getExternalSnowflakeCluster,
    getSchemaList,
    navigate,
    user.User_id,
    workspaceName,
  ]);

  useEffect(() => {
    additionalDataSections.forEach(({ key, includeResult, queryResultAs, variable }) => {
      const lowerVar = variable?.toLowerCase();
      const isMissingVariable = includeResult && queryResultAs === 2 && !variable;
      const isDuplicate = lowerVar && variableNames.filter((name) => name === lowerVar).length > 1;
      setVariableError((prev) => ({ ...prev, [key]: { ...prev[key], isMissingVariable, isDuplicate } }));
    });
  }, [additionalDataSections, variableNames]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    { link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`, displayName: "Data Pipelines", active: false },
    { link: "#", displayName: Job_id ? "Edit Notification Job" : "Add Notification Job", active: true },
  ];

  const disableSave = useMemo(
    () =>
      jobState.loading ||
      jobState.jobName === "" ||
      jobState.subject === "" ||
      jobState.body === "" ||
      emails.length === 0 ||
      (nextJobState.nextJobToggle === true && !(nextJobState.nextJobId || nextJobState.errorJobId)) ||
      // Duplication check for variable name and file name
      new Set(variableNames).size !== variableNames.length ||
      new Set(fileNames).size !== fileNames.length ||
      // Conditional check for each section of additional data
      additionalDataSections.some((section) => {
        const { includeResult, query, queryError, Cluster, runJobAsUserId, database, schema, location } = section;
        const userNotExist = variableError[section.key]?.userNotExist;

        return (
          (Job_id && location === "SNOWFLAKE" && includeResult && userNotExist) ||
          (includeResult &&
            (query === "" || queryError || !Cluster || runJobAsUserId === "" || database === "" || schema === ""))
        );
      }),
    [
      jobState.loading,
      jobState.jobName,
      jobState.subject,
      jobState.body,
      emails.length,
      nextJobState.nextJobToggle,
      nextJobState.nextJobId,
      nextJobState.errorJobId,
      variableNames,
      fileNames,
      additionalDataSections,
      Job_id,
      variableError,
    ]
  );

  const toggleSchedule = useCallback(
    () => setJobState((prev) => ({ ...prev, scheduleEnabled: !prev.scheduleEnabled })),
    []
  );

  return (
    <CommonLayout title="Job Scheduler" loading={jobState.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          height: "100vh",
          overflowX: "hidden",
          overflowY: "auto",
          paddingBottom: "20px",
        }}
        id="main_box"
      >
        {initialLoading ? (
          <CircularProgress
            color="primary"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        ) : (
          <>
            <Backdrop
              sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={jobState.loading || nextJobState.loading}
            >
              <CircularProgress color="primary" />
            </Backdrop>
            <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
              <Box className={classes.PanelContainer}>
                <Box sx={{ display: "flex", alingItems: "center", mb: 2 }}>
                  {Job_id ? (
                    <Typography className={classes.HeaderTitle} gutterBottom component="div">
                      Edit Notification Job
                    </Typography>
                  ) : (
                    <Typography className={classes.HeaderTitle} gutterBottom component="div">
                      Add Notification Job
                    </Typography>
                  )}
                  <ButtonUI
                    onClick={handleSave}
                    variant="contained"
                    color="success"
                    disabled={disableSave}
                    sx={{ mb: "5px" }}
                  >
                    Save
                  </ButtonUI>
                  <ButtonUI
                    onClick={handleCancelClick}
                    variant="contained"
                    color="secondary"
                    sx={{ mb: "5px", ml: "15px" }}
                  >
                    Cancel
                  </ButtonUI>

                  {Job_id ? (
                    <>
                      <ButtonUI
                        onClick={() => setOpenClone(true)}
                        variant="contained"
                        color="info"
                        sx={{ mb: "5px", mx: "15px" }}
                        disabled={disableSave}
                      >
                        Clone
                      </ButtonUI>

                      <ButtonUI
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setPrevState(setDeleteJobState, {
                            openConfirm: true,
                            confirmTitle: "Delete Notification Job",
                            confirmMessage: `Are you sure you want to delete "${jobState.name}" Job?"`,
                          });
                        }}
                        disabled={!Job_id}
                        sx={{ mb: "5px" }}
                      >
                        Delete
                      </ButtonUI>
                    </>
                  ) : (
                    <></>
                  )}

                  <Box>
                    <HelpContextIcon screen="Notification Job" />
                  </Box>
                </Box>

                <FormTile style={{ overflowX: "auto" }}>
                  <AddJobForm
                    jobState={jobState}
                    setJobState={setJobState}
                    dataPipelineList={dataPipelineList}
                    confirmPipelineChange={confirmPipelineChange}
                    Job_id={Job_id}
                  />
                </FormTile>

                <Typography variant="body1" className={classes.PanelTitle}>
                  Email Content
                </Typography>
                <FormTile style={{ overflowX: "auto" }}>
                  <TextField
                    value={jobState.subject}
                    sx={{ width: "100%", mb: 2 }}
                    required
                    label="Subject"
                    onChange={(e) => setPrevState(setJobState, { subject: e.target.value })}
                    placeholder="Here is your requested report"
                  />

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
                    <MultipleChipInput
                      placeholder="Enter Email Recipients"
                      value={emails ?? []}
                      inputValue={inputValues ?? ""}
                      helperText="Press Enter or use commas to add email addresses separately."
                      handleonInputChange={(event, newInputValue) => setInputValues(newInputValue)}
                      handleOnChange={(event, newValue) => handleEmailChange(newValue)}
                      onDelete={(option) => onDelete(option)}
                      handleKeyPress={(e) => handleKeyPress(e)}
                      handlePaste={(e) => handlePaste(e)}
                      disableClearable={true}
                      maxHeight="380px"
                    />
                    <ButtonUI
                      variant="text"
                      color="secondary"
                      style={{ margin: "0px 10px 7px 0", fontSize: "14px", minWidth: "100px" }}
                      onMouseDown={(e) => handleClear()}
                      disabled={!emails?.length}
                    >
                      Clear
                    </ButtonUI>
                  </Box>

                  <Box sx={{ marginBottom: "50px" }}>
                    <ComponentSuspense>
                      <KMKTextEditor
                        editorRef={emailBodyref}
                        placeholder="Hi, this is an alert regarding..."
                        value={jobState.body}
                        setValue={(content) => {
                          setPrevState(setJobState, { body: content });
                        }}
                        style={{ height: "250px", width: "100%" }}
                        theme="snow"
                      />
                    </ComponentSuspense>
                  </Box>
                </FormTile>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="body1" className={classes.PanelTitle}>
                    Additional Data Sections (optional)
                  </Typography>
                </Box>

                <FormTile>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", margin: "6px" }}>
                    <ButtonUI
                      endIcon={<AddIcon />}
                      variant="contained"
                      color="info"
                      style={{ width: "auto" }}
                      onClick={generateName}
                      disabled={additionalDataSections.length >= 5 || jobState.loading}
                    >
                      Add Section
                    </ButtonUI>
                  </Box>

                  {selectedSectionIndex > -1 && (
                    <AdditionalData
                      additionalDataSections={additionalDataSections}
                      additionalState={additionalDataSections[selectedSectionIndex]}
                      getSchemaList={getSchemaList}
                      locationClusters={locationClustersRef.current}
                      locationSchemas={locationSchemasRef.current}
                      locations={locations}
                      participantsExcludeVisitor={participantsExcludeVisitor}
                      selectedKey={selectedKey}
                      setAdditionalState={setAdditionalDataKeys}
                      setJobState={setJobState}
                      updateClusterList={(location, clusters) => (locationClustersRef.current[location] = clusters)}
                      setSelectedKey={setSelectedKey}
                      setVariableError={setVariableError}
                      variableError={variableError}
                      section={selectedSectionIndex + 1}
                    />
                  )}
                </FormTile>

                <Typography variant="body1" className={classes.PanelTitle}>
                  Schedule
                </Typography>
                <FormTile style={{ overflowX: "auto" }}>
                  <Scheduler
                    scheduleEnabled={jobState.scheduleEnabled}
                    cronValue={values.cronValue}
                    dispatchValues={dispatchValues}
                    toggleValue={toggleSchedule}
                    timeZone={jobState.timeZone}
                    nextRunTime={jobState.nextRunTime}
                  />
                </FormTile>

                <Typography variant="body1" className={classes.PanelTitle}>
                  Next Job
                </Typography>
                <FormTile style={{ overflowX: "auto", marginBottom: "12px" }}>
                  <NextJobForm
                    dataPipelineList={dataPipelineList}
                    nextJobToggle={nextJobState.nextJobToggle}
                    nextDataPipelineId={nextJobState.nextDataPipelineId}
                    nextJobId={nextJobState.nextJobId}
                    errorDataPipelineId={nextJobState.errorDataPipelineId}
                    errorJobId={nextJobState.errorJobId}
                    setNextJobState={setNextJobState}
                  />
                </FormTile>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {openClone && (
        <CloneJob
          setOpenClone={setOpenClone}
          pipelineId={jobState.DataPipeline_id}
          handleSave={handleSave}
          JobType={JOB_BY_TYPE.notification}
        />
      )}

      {deleteJobState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: deleteJobState.confirmTitle,
            popupMessage: deleteJobState.confirmMessage,
            handleCancel: handleCancel,
            handleClick: deleteJob,
            loadingForConfirm: deleteJobState.loadingForConfirm,
          }}
        />
      )}
    </CommonLayout>
  );
}
